/**
 * ExportFieldPicker — field selection step for export (plan section 31).
 *
 * Offers three modes:
 * - recommended: descriptor-driven default set
 * - current: visible table columns (excluding maintenance columns)
 * - custom: searchable grouped checkbox list of all exportable fields
 */
import { useCallback, useId, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { resolveTKey } from '../../../i18n/resolve-key';

export type FieldSelectionMode = 'recommended' | 'current' | 'custom';

export interface ExportFieldGroup {
  groupId: string;
  label: string;
  fields: Array<{
    fieldId: string;
    label: string;
    recommended?: boolean;
  }>;
}

export interface ExportFieldPickerProps {
  mode: FieldSelectionMode;
  onModeChange: (mode: FieldSelectionMode) => void;
  selectedFields: string[];
  onFieldsChange: (fields: string[]) => void;
  fieldGroups: ExportFieldGroup[];
  availableModes?: FieldSelectionMode[];
}

const DEFAULT_MODES: FieldSelectionMode[] = ['recommended', 'current', 'custom'];

export function ExportFieldPicker({
  mode,
  onModeChange,
  selectedFields,
  onFieldsChange,
  fieldGroups,
  availableModes = DEFAULT_MODES,
}: ExportFieldPickerProps) {
  const { t } = useTranslation();
  const searchId = useId();
  const [search, setSearch] = useState('');

  const allFields = useMemo(() => fieldGroups.flatMap((g) => g.fields), [fieldGroups]);
  const recommendedFields = useMemo(
    () => allFields.filter((f) => f.recommended).map((f) => f.fieldId),
    [allFields],
  );
  const selectedSet = useMemo(() => new Set(selectedFields), [selectedFields]);

  const filteredGroups = useMemo(() => {
    if (!search.trim()) return fieldGroups;
    const q = search.toLowerCase();
    return fieldGroups
      .map((group) => ({
        ...group,
        fields: group.fields.filter(
          (f) => f.label.toLowerCase().includes(q) || f.fieldId.toLowerCase().includes(q),
        ),
      }))
      .filter((group) => group.fields.length > 0);
  }, [fieldGroups, search]);

  const handleToggle = useCallback(
    (fieldId: string) => {
      const next = new Set(selectedSet);
      if (next.has(fieldId)) next.delete(fieldId);
      else next.add(fieldId);
      onFieldsChange([...next]);
    },
    [selectedSet, onFieldsChange],
  );

  const modeLabel = (m: FieldSelectionMode): string => {
    switch (m) {
      case 'recommended': return t(resolveTKey('tableExport.fields.recommended'));
      case 'current': return t(resolveTKey('tableExport.fields.current'));
      case 'custom': return t(resolveTKey('tableExport.fields.custom'));
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {t(resolveTKey('tableExport.fields.title'))}
      </Typography>

      <RadioGroup
        value={mode}
        onChange={(_, v) => onModeChange(v as FieldSelectionMode)}
        aria-label={t(resolveTKey('tableExport.fields.title'))}
        sx={{ mb: mode === 'custom' ? 2 : 0 }}
      >
        {availableModes.map((m) => (
          <FormControlLabel
            key={m}
            value={m}
            control={<Radio size="small" />}
            label={modeLabel(m)}
          />
        ))}
      </RadioGroup>

      {mode === 'custom' && (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
            <Chip
              size="small"
              label={t(resolveTKey('tableExport.fields.selectRecommended'))}
              onClick={() => onFieldsChange(recommendedFields)}
            />
            <Chip
              size="small"
              label={t(resolveTKey('tableExport.fields.selectAll'))}
              onClick={() => onFieldsChange(allFields.map((f) => f.fieldId))}
            />
            <Chip
              size="small"
              label={t(resolveTKey('tableExport.fields.clear'))}
              onClick={() => onFieldsChange([])}
            />
          </Box>

          <TextField
            id={searchId}
            size="small"
            fullWidth
            placeholder={t(resolveTKey('tableExport.fields.search'))}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ mb: 2 }}
          />

          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            {t(resolveTKey('tableExport.fields.selectedCount'), { count: selectedFields.length, total: allFields.length })}
          </Typography>

          {filteredGroups.map((group) => (
            <Box key={group.groupId} sx={{ mb: 2 }}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                {group.label}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 0 }}>
                {group.fields.map((field) => (
                  <FormControlLabel
                    key={field.fieldId}
                    control={
                      <Checkbox
                        size="small"
                        checked={selectedSet.has(field.fieldId)}
                        onChange={() => handleToggle(field.fieldId)}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2">{field.label}</Typography>
                        {field.recommended && (
                          <Chip size="small" label="★" sx={{ height: 16, fontSize: 10 }} />
                        )}
                      </Box>
                    }
                  />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
